import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { LineItem, PackageEntrySource } from "~/features/cart/cart/models/CartData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { NetPriceData, PriceData } from "~/domains/pricing/types/PriceData";

import {
  findMinOrderQuantity,
  getDefaultQuantity,
  useMOQValidation,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { isValidQuantity } from "~/utils/packageEntryUtils";
import { isMinOrderQuantityRestrictionEnabled, isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import {
  getPriceInfo,
  getSurchargeValueFromTotalPrice,
  getTotalPriceForLineItem,
  isProductPMA,
  productHasInStock,
} from "~/domains/product/utils/productDescriptorUtils";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { isKITKITProduct } from "~/domains/product/types/ProductData";
import { isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";
import { PHASE_IN_BUY_ERROR_KEY } from "~/features/productDetails/components/PhaseInOut/constants";

import * as PriceUtils from "~/utils/PriceUtils";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { CART_QTY_EXCEEDS_SALES_ERROR_KEY } from "~/domains/salesCampaign/constants";
import { processPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";

export interface UseLineItemDataProps {
  lineItem: LineItem;
  product: ProductDescriptor;
}

export interface LineItemMessages {
  type: "info" | "warning" | "error" | "fatal";
  key: string;
  params?: Record<string, unknown>;
}

export interface LineItemPriceData {
  /** Formatted total price string (e.g., "€1,234.56") or "..." if loading */
  totalPriceFormatted: string;
  /** Raw total price value for calculations */
  totalPrice: PriceData | null;
  /** Surcharge value if applicable */
  surchargeValue: PriceData | undefined;
  /** Whether product has a surcharge */
  hasSurcharge: boolean;
  /** Tariff surcharge flag */
  tariffSurcharge: boolean;
  /** Net price data */
  netPrice: NetPriceData | undefined;
  /** Price unit (quantity per price) */
  priceUnit: number;
  /** Whether this is a special price */
  isSpecialPrice: boolean;
  /** Zero price label to display */
  zeroPriceLabel: string;
}

export interface LineItemSalesData {
  /** Whether the product package is on sale */
  isOnSale: boolean;
  /** Whether sale is currently active for this line item */
  saleActive: boolean;
  /** Available sales stock quantity */
  salesStock: number;
  /** Processed sales pricing details when sale is active */
  salesPricing: ReturnType<typeof processPricingDetails> | null;
}

export interface LineItemValidation {
  /** Whether quantity input should show error state */
  quantityHasError: boolean;
  /** Whether package size is invalid based on MOQ rules */
  isPackageSizeInvalid: boolean;
  /** Helper label for MOQ validation */
  MOQHelperLabel: string | undefined;
  /** Minimum quantity value allowed */
  minQuantityValue: number;
  /** Whether line item is valid */
  isValid: boolean;
  /** Whether editing is disabled due to fatal errors */
  disableEditing: boolean;
}

export interface LineItemFlags {
  /** Whether product is in stock */
  inStock: boolean;
  /** Whether product is PMA type */
  isPMA: boolean;
  /** Whether product is a KIT product */
  isKIT: boolean;
  /** Whether quantity is visible/editable */
  quantityVisible: boolean;
  /** Whether comments can be added */
  canSeeComment: boolean;
  /** Whether product is an Optime solution */
  isOptime: boolean;
  /** Whether line item is from bulk upload */
  isBulkUpload: boolean;
  /** Whether line item is added from a quote */
  isAddedFromQuote: boolean;
  /** Whether line item has restricted package selection */
  restrictedPackage: boolean;
  /** Whether better deal is available */
  showBetterDeal: boolean;
}

export interface UseLineItemDataResult {
  /** Price-related computed data */
  pricing: LineItemPriceData;
  /** Sales campaign related data */
  sales: LineItemSalesData;
  /** Validation states */
  validation: LineItemValidation;
  /** Various boolean flags */
  flags: LineItemFlags;
  /** Messages to display for this line item */
  messages: LineItemMessages[];
  /** Pack size from line item */
  packSize: number;
  /** Min order quantity for the selected package */
  minOrderQuantity: number;
}

/**
 * Hook that computes all derived data for a line item.
 * Can be used by both EditablePackageEntry and RegularProductLine components.
 *
 * @param props - Line item and product descriptor
 * @returns Computed data including pricing, validation, sales data, and flags
 */
export function useLineItemData({ lineItem, product }: UseLineItemDataProps): UseLineItemDataResult {
  const { t } = useTranslation();

  const packSize = lineItem.packSize ?? 1;
  const minOrderQuantity = findMinOrderQuantity(product, lineItem.packCode);

  // Sales helper
  const salesHelper = useSalesHelper(product);
  const salesStock = salesHelper.forPackage(lineItem.packCode)?.stock ?? 0;
  const isOnSale = salesHelper.isPackageOnSale(lineItem.packCode);
  const saleActive = lineItem.saleCampaignEnabled ?? false;

  // Flags
  const isAddedFromQuote = lineItem.quoteLine !== "" && lineItem.quoteNumber !== "";
  const isBulkUpload = lineItem.source === ("BULK" as PackageEntrySource);
  const quantityVisible = product.base.quantityVisible;
  const canSeeComment = canUseCommentsForProduct(product.base.marketType);
  const isOptime = product.base.isOptime;
  const inStock = productHasInStock(product);
  const isPMA = isProductPMA(product);
  const isKIT = isKITKITProduct(product.base);
  const restrictedPackage = lineItem.restrictedPackage;

  // MOQ validation
  const isMOQCheckEnabled = isMinOrderQuantityRestrictionEnabled() && !saleActive && !isAddedFromQuote;
  const { isPackageSizeInvalid, MOQHelperLabel } = useMOQValidation({
    quantity: lineItem.quantity,
    packSize,
    minOrderQuantity,
    isAvailabilityLoaded: !!product.availability,
    isPackageSelected: !!lineItem.packCode,
    isOnSale: saleActive,
  });

  // Basic quantity validation
  const validQty = isValidQuantity(packSize, lineItem.quantity);

  // Validation state
  const validation = useMemo((): LineItemValidation => {
    const isSizeInvalid = isMOQCheckEnabled ? isPackageSizeInvalid : !validQty;

    let quantityHasError = false;
    if (isSizeInvalid) {
      quantityHasError = true;
    } else if (lineItem.messages.find((m) => m.key === PHASE_IN_BUY_ERROR_KEY)) {
      quantityHasError = true;
    } else if (lineItem.messages.find((m) => m.key === CART_QTY_EXCEEDS_SALES_ERROR_KEY)) {
      quantityHasError = true;
    }

    const disableEditing = !!lineItem.messages.find((msg) => msg.type === "fatal");
    const minQuantityValue = isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize;

    return {
      quantityHasError,
      isPackageSizeInvalid,
      MOQHelperLabel: isMOQCheckEnabled ? MOQHelperLabel : undefined,
      minQuantityValue,
      isValid: lineItem.valid,
      disableEditing,
    };
  }, [
    isMOQCheckEnabled,
    isPackageSizeInvalid,
    validQty,
    lineItem.messages,
    lineItem.valid,
    packSize,
    minOrderQuantity,
    MOQHelperLabel,
  ]);

  // Price calculations
  const pricing = useMemo((): LineItemPriceData => {
    const netPrice = lineItem.netPrice;
    const priceUnit = netPrice?.unit ?? 1;
    const zeroPriceLabel = isPMA || isKIT ? "-" : t("product.priceOnRequest");
    const isSpecial = isSpecialPrice(product, lineItem.packCode);
    const tariffSurcharge = isTariffSurchargeEnabled() && (netPrice?.tariffSurcharge ?? false);

    if (isBulkUpload || !netPrice) {
      return {
        totalPriceFormatted: "...",
        totalPrice: null,
        surchargeValue: undefined,
        hasSurcharge: false,
        tariffSurcharge,
        netPrice: undefined,
        priceUnit,
        isSpecialPrice: isSpecial,
        zeroPriceLabel,
      };
    }

    const hasSurcharge = (netPrice.surcharge ?? 0) > 0 && netPrice.value > 0;
    const totalPriceVal = getTotalPriceForLineItem(lineItem, product);
    const surchargeValue = hasSurcharge ? getSurchargeValueFromTotalPrice(totalPriceVal) : undefined;
    const totalPriceFormatted = PriceUtils.text(totalPriceVal, "-");

    return {
      totalPriceFormatted,
      totalPrice: totalPriceVal,
      surchargeValue,
      hasSurcharge,
      tariffSurcharge,
      netPrice,
      priceUnit,
      isSpecialPrice: isSpecial,
      zeroPriceLabel,
    };
  }, [lineItem, product, isBulkUpload, isPMA, isKIT, t]);

  // Sales data
  const sales = useMemo((): LineItemSalesData => {
    const salesPricing =
      saleActive && lineItem.actualNetPrice && lineItem.actualSalePrice
        ? processPricingDetails({
            netPrice: lineItem.actualNetPrice as NetPriceData,
            salesPrice: lineItem.actualSalePrice,
            saleActivated: saleActive,
          })
        : null;

    return {
      isOnSale,
      saleActive,
      salesStock,
      salesPricing,
    };
  }, [isOnSale, saleActive, salesStock, lineItem.actualNetPrice, lineItem.actualSalePrice]);

  // Messages
  const messages = useMemo((): LineItemMessages[] => {
    let msgs = lineItem.messages.slice() as LineItemMessages[];

    // Better deal message (computed here for simplicity, but could be passed in)
    // This logic mirrors selectShowBetterDeal behavior
    const priceInfo = getPriceInfo({ product, packCode: lineItem.packCode });
    const showBetterDeal =
      priceInfo && priceInfo.minQtyAvailableScale !== null && lineItem.quantity >= priceInfo.minQtyAvailableScale;

    if (showBetterDeal) {
      msgs.push({
        type: "info",
        key: "addProduct.scalingDeal",
      });
    }

    // Filter to only fatal messages if any exist
    const fatalMessages = msgs.filter((msg) => msg.type === "fatal");
    if (fatalMessages.length > 0 || (lineItem.contractErrorMessage && lineItem.contractErrorMessage.type === "fatal")) {
      msgs = fatalMessages;
    }

    return msgs;
  }, [lineItem.messages, lineItem.contractErrorMessage, product, lineItem.packCode, lineItem.quantity]);

  // Flags
  const flags = useMemo((): LineItemFlags => {
    const priceInfo = getPriceInfo({ product, packCode: lineItem.packCode });
    const showBetterDeal =
      priceInfo && priceInfo.minQtyAvailableScale !== null && lineItem.quantity >= priceInfo.minQtyAvailableScale;

    return {
      inStock,
      isPMA,
      isKIT,
      quantityVisible,
      canSeeComment,
      isOptime,
      isBulkUpload,
      isAddedFromQuote,
      restrictedPackage,
      showBetterDeal: showBetterDeal ?? false,
    };
  }, [
    inStock,
    isPMA,
    isKIT,
    quantityVisible,
    canSeeComment,
    isOptime,
    isBulkUpload,
    isAddedFromQuote,
    restrictedPackage,
    product,
    lineItem.packCode,
    lineItem.quantity,
  ]);

  return {
    pricing,
    sales,
    validation,
    flags,
    messages,
    packSize,
    minOrderQuantity,
  };
}
